import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, dateFmt } from "@/lib/api";
import {
    ShieldAlert, Filter, User, Receipt, FileText, Truck,
    CreditCard, Activity, AlertTriangle, RefreshCw, Eye, Download
} from "lucide-react";
import HelpTip from "@/components/HelpTip";
import { SECTION_HELP } from "@/lib/helpTexts";

const ENTITY_ICONS = {
    service: Activity,
    invoice: Receipt,
    payment: CreditCard,
    payable: CreditCard,
    frap: FileText,
    unit: Truck,
    user: User,
    incident: AlertTriangle,
};

const ENTITY_LINKS = {
    service: (id) => `/services/${id}`,
    invoice: () => "/receivable",
    payable: () => "/payable",
    frap: () => "/services",
    unit: () => "/units",
};

const ACTION_COLORS = {
    create: "bg-emerald-100 text-emerald-700",
    update: "bg-blue-100 text-blue-700",
    delete: "bg-red-100 text-red-700",
    status_change: "bg-amber-100 text-amber-800",
    assign: "bg-indigo-100 text-indigo-700",
    payment: "bg-emerald-100 text-emerald-700",
    reject: "bg-red-100 text-red-700",
    approve: "bg-emerald-100 text-emerald-700",
};

const PERIOD_OPTIONS = [
    { value: 1, label: "Hoy" },
    { value: 7, label: "7 días" },
    { value: 30, label: "30 días" },
    { value: 90, label: "90 días" },
];

const ENTITY_OPTIONS = ["service", "invoice", "payment", "payable", "frap", "unit", "user", "incident"];
const ACTION_OPTIONS = ["create", "update", "delete", "status_change", "assign", "payment"];

export default function Audit() {
    const [days, setDays] = useState(7);
    const [entityFilter, setEntityFilter] = useState("");
    const [actionFilter, setActionFilter] = useState("");
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [unauthorized, setUnauthorized] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ days: String(days), limit: "200" });
            if (entityFilter) params.append("entity", entityFilter);
            if (actionFilter) params.append("action", actionFilter);
            const [logRes, sumRes] = await Promise.all([
                api.get(`/audit?${params.toString()}`),
                api.get(`/audit-stats/summary?days=${days}`),
            ]);
            setItems(logRes.data || []);
            setSummary(sumRes.data);
            setUnauthorized(false);
        } catch (e) {
            if (e?.response?.status === 403) setUnauthorized(true);
        } finally {
            setLoading(false);
        }
    }, [days, entityFilter, actionFilter]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (item) => {
        try {
            const { data } = await api.get(`/audit/${item.id}`);
            setSelected(data);
        } catch (e) {
            setSelected(item);
        }
    };

    if (unauthorized) {
        return (
            <div className="fm-card p-10 text-center" data-testid="audit-unauthorized">
                <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-red-400" strokeWidth={1.5} />
                <h2 className="font-display font-bold text-lg mb-1">Acceso restringido</h2>
                <p className="text-sm text-slate-500">Solo personal administrativo puede consultar la bitácora.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="audit-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <ShieldAlert className="w-6 h-6 text-red-600" /> Auditoría y Trazabilidad
                        <HelpTip text={SECTION_HELP.audit_log} variant="?" />
                    </h2>
                    <div className="text-xs text-slate-500 font-mono">
                        Bitácora global · {items.length} eventos en últimos {days} día(s)
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            const rows = [["Fecha", "Usuario", "Entidad", "ID Entidad", "Acción", "Resumen"]];
                            items.forEach((it) => rows.push([
                                it.at,
                                it.user_name || it.user_id || "—",
                                it.entity,
                                it.entity_id,
                                it.action,
                                (it.summary || "").replace(/[\n,]/g, " "),
                            ]));
                            const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
                            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `FindMed-Auditoria-${new Date().toISOString().slice(0,10)}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                        }}
                        className="fm-btn-outline text-xs"
                        data-testid="export-audit-csv"
                        title="Descarga los eventos visibles (con filtros aplicados) en CSV"
                    >
                        <Download className="w-3.5 h-3.5" /> Exportar CSV
                    </button>
                    <button onClick={load} disabled={loading} className="fm-btn-outline text-xs">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
                    </button>
                </div>
            </div>

            {/* Summary KPIs */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-kpis">
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total eventos</div>
                        <div className="font-mono font-black text-3xl">{summary.total}</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Servicios</div>
                        <div className="font-mono font-black text-3xl">{summary.by_entity.service || 0}</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Movs. financieros</div>
                        <div className="font-mono font-black text-3xl">{(summary.by_entity.invoice || 0) + (summary.by_entity.payment || 0) + (summary.by_entity.payable || 0)}</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Top usuario</div>
                        <div className="font-mono font-bold text-base truncate">
                            {summary.top_users[0]?.[0] || "—"}
                        </div>
                        <div className="text-xs text-slate-500">{summary.top_users[0]?.[1] || 0} eventos</div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="fm-card p-3 flex items-center gap-3 flex-wrap" data-testid="audit-filters">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <Filter className="w-3.5 h-3.5" /> Filtros:
                </div>

                <div className="flex items-center gap-1 fm-card p-1">
                    {PERIOD_OPTIONS.map((p) => (
                        <button
                            key={p.value}
                            type="button"
                            onClick={() => setDays(p.value)}
                            data-testid={`audit-period-${p.value}`}
                            className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${days === p.value ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                <select
                    value={entityFilter}
                    onChange={(e) => setEntityFilter(e.target.value)}
                    className="fm-input text-xs max-w-xs"
                    data-testid="audit-entity-filter"
                >
                    <option value="">Todas las entidades</option>
                    {ENTITY_OPTIONS.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>

                <select
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    className="fm-input text-xs max-w-xs"
                    data-testid="audit-action-filter"
                >
                    <option value="">Todas las acciones</option>
                    {ACTION_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
            </div>

            {/* List */}
            <div className="fm-card overflow-hidden" data-testid="audit-list">
                {items.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">
                        <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Sin eventos para los filtros actuales.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {["Fecha", "Usuario", "Entidad", "Acción", "Resumen", "Cambios", ""].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => {
                                const Icon = ENTITY_ICONS[it.entity] || ShieldAlert;
                                const linkFn = ENTITY_LINKS[it.entity];
                                const entityLink = linkFn ? linkFn(it.entity_id) : null;
                                return (
                                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${it.id}`}>
                                        <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">
                                            {dateFmt(it.at).replace(",", "·")}
                                        </td>
                                        <td className="py-2 px-3 text-xs">
                                            <div className="font-medium">{it.user_name || "Sistema"}</div>
                                            <div className="text-[10px] text-slate-400 uppercase tracking-wider">{it.user_role || ""}</div>
                                        </td>
                                        <td className="py-2 px-3">
                                            <span className="inline-flex items-center gap-1.5 text-xs uppercase font-bold tracking-wider text-slate-700">
                                                <Icon className="w-3.5 h-3.5" /> {it.entity}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3">
                                            <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] uppercase font-bold tracking-wider ${ACTION_COLORS[it.action] || "bg-slate-200 text-slate-700"}`}>
                                                {it.action}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 text-xs max-w-md">
                                            {it.summary || "—"}
                                            {it.meta?.folio && <div className="text-[10px] text-slate-400 font-mono">Folio: {it.meta.folio}</div>}
                                        </td>
                                        <td className="py-2 px-3">
                                            {(it.diff && it.diff.length > 0)
                                                ? <span className="text-xs font-mono font-bold">{it.diff.length} campo(s)</span>
                                                : <span className="text-xs text-slate-400">—</span>}
                                        </td>
                                        <td className="py-2 px-3">
                                            <div className="flex items-center gap-1.5 justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => openDetail(it)}
                                                    className="p-1.5 hover:bg-slate-200 rounded-sm"
                                                    data-testid={`audit-detail-${it.id}`}
                                                    aria-label="Ver detalle"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                </button>
                                                {entityLink && (
                                                    <Link
                                                        to={entityLink}
                                                        className="p-1.5 hover:bg-slate-200 rounded-sm text-slate-700"
                                                        title="Ir al objeto"
                                                    >
                                                        →
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Detail modal */}
            {selected && (
                <div
                    className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setSelected(null)}
                    data-testid="audit-detail-modal"
                >
                    <div className="max-w-3xl w-full bg-white rounded-sm overflow-hidden max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Auditoría</div>
                                <div className="font-display font-bold">{selected.summary || `${selected.entity} · ${selected.action}`}</div>
                                <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                                    {dateFmt(selected.at)} · por {selected.user_name} ({selected.user_role})
                                </div>
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className="text-white hover:bg-slate-800 px-3 py-1 rounded-sm text-xs uppercase tracking-wider font-bold"
                            >
                                Cerrar
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-5 space-y-4 bg-slate-50">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div><span className="font-bold uppercase tracking-wider text-slate-500">Entidad:</span> {selected.entity} · {selected.entity_id}</div>
                                <div><span className="font-bold uppercase tracking-wider text-slate-500">Acción:</span> {selected.action}</div>
                            </div>

                            {(selected.diff || []).length > 0 && (
                                <div className="fm-card p-4 bg-white">
                                    <h4 className="font-display font-bold text-sm mb-3">Cambios</h4>
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-200">
                                                <th className="text-left py-1 px-2 text-[10px] font-bold uppercase text-slate-500">Campo</th>
                                                <th className="text-left py-1 px-2 text-[10px] font-bold uppercase text-slate-500">Antes</th>
                                                <th className="text-left py-1 px-2 text-[10px] font-bold uppercase text-slate-500">Después</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selected.diff.map((d, i) => (
                                                <tr key={i} className="border-b border-slate-100">
                                                    <td className="py-1 px-2 font-mono font-bold">{d.field}</td>
                                                    <td className="py-1 px-2 font-mono text-red-700 break-all max-w-xs">{JSON.stringify(d.before)}</td>
                                                    <td className="py-1 px-2 font-mono text-emerald-700 break-all max-w-xs">{JSON.stringify(d.after)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selected.meta && Object.keys(selected.meta).length > 0 && (
                                <div className="fm-card p-4 bg-white">
                                    <h4 className="font-display font-bold text-sm mb-3">Metadata</h4>
                                    <pre className="text-xs font-mono bg-slate-50 p-3 rounded-sm overflow-auto">
                                        {JSON.stringify(selected.meta, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
